import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomModal from "../modal/customModal";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import ImageCropModal from "./ImageCropModal";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import "./mediaLibraryModal.scss";

const PAGE_SIZE = 24;
/** Above library modal (9999+) and typical Bootstrap overlays (~1055). */
const CROP_Z_INDEX_BASE = 13000;

const MediaLibraryModal = ({
  isOpen,
  onClose,
  onSelect,
  title = "Images",
  subtitle = "Upload to your library, then click an image to crop and select it for the product.",
  enableCrop = true,
  cropAspect = 1,
  cropModalTitle = "Crop image",
}) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const uploadInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("library");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim());
  const [pageNumber, setPageNumber] = useState(1);
  const [items, setItems] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [uploadAltText, setUploadAltText] = useState("");

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [pendingCropSrc, setPendingCropSrc] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  /** Alt text / name when cropping a library pick (not an upload). */
  const [pendingSelectMeta, setPendingSelectMeta] = useState(null);

  const rootClass = `media-library-modal ${isDarkMode ? "media-library-modal--dark" : "media-library-modal--light"}`;

  useEffect(() => {
    if (isOpen) {
      setActiveTab("library");
      setPageNumber(1);
      setSearch("");
      setCropModalOpen(false);
      setPendingCropSrc("");
      setPendingFile(null);
      setPendingSelectMeta(null);
      setUploadAltText("");
    }
  }, [isOpen]);

  const loadMedia = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIs.GET_COMPANY_MEDIA_LIST, {
        params: {
          search: debouncedSearch,
          pageNumber,
          pageSize: PAGE_SIZE,
        },
      });
      if (!res.data?.status) {
        showErrorToast(res.data?.message || "Could not load media library");
        setItems([]);
        setTotalRecords(0);
        return;
      }
      const payload = res.data?.data || {};
      setItems(payload.items || []);
      setTotalRecords(payload.totalRecords ?? 0);
    } catch {
      showErrorToast("Could not load media library");
      setItems([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [isOpen, debouncedSearch, pageNumber, refreshNonce]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  const finishSelectAndClose = (filePath, altText = "") => {
    if (!filePath) return;
    onSelect(filePath, altText || "");
    onClose();
  };

  const postFileToLibrary = async (file, altText = "") => {
    const fd = new FormData();
    fd.append("file", file);
    const trimmed = (altText || "").trim();
    if (trimmed) fd.append("altText", trimmed.slice(0, 500));
    const res = await axiosInstance.post(APIs.POST_COMPANY_MEDIA_UPLOAD, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (!res.data?.status) {
      showErrorToast(res.data?.message || "Upload failed");
      return null;
    }
    return res.data?.data?.filePath || null;
  };

  const handleUploadFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (uploadInputRef.current) uploadInputRef.current.value = "";
    if (!file) return;

    // Upload stores the original — crop happens when selecting from the Library tab
    setUploading(true);
    try {
      const path = await postFileToLibrary(file, uploadAltText);
      if (path) {
        showSuccessToast(
          enableCrop
            ? "Image added to library — open Library and click it to crop and select."
            : "Image added to library — pick it from the Library tab."
        );
        setRefreshNonce((n) => n + 1);
        setActiveTab("library");
        setPageNumber(1);
        setUploadAltText("");
      }
    } catch {
      showErrorToast("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const closeCropModal = () => {
    if (pendingSelectMeta?.objectUrl) {
      try {
        URL.revokeObjectURL(pendingSelectMeta.objectUrl);
      } catch {
        /* ignore */
      }
    }
    setCropModalOpen(false);
    setPendingCropSrc("");
    setPendingFile(null);
    setPendingSelectMeta(null);
  };

  const beginCropForLibraryItem = async (item) => {
    if (!item?.mediaId && !item?.MediaId) return;
    const mediaId = item.mediaId ?? item.MediaId;
    setUploading(true);
    try {
      // Authenticated same-API blob fetch — avoids static /uploads CORS blocking crop canvas
      const res = await axiosInstance.get(APIs.GET_COMPANY_MEDIA_FILE(mediaId), {
        responseType: "blob",
        timeout: 60000,
      });
      if (res.status !== 200 || !(res.data instanceof Blob)) {
        const msg =
          res.status === 404
            ? "Image file is missing on the server"
            : "Could not load image for cropping";
        showErrorToast(msg);
        return;
      }
      const blob = res.data;
      if (!blob.type || !blob.type.startsWith("image/")) {
        // API error JSON may arrive as a blob when responseType is blob
        showErrorToast("Could not load image for cropping");
        return;
      }
      const name = item.originalFileName || "library-image.jpg";
      const mime = blob.type.startsWith("image/") ? blob.type : "image/jpeg";
      const file = new File([blob], name, { type: mime });
      const objectUrl = URL.createObjectURL(blob);
      setPendingSelectMeta({
        altText: item.altText || "",
        originalFileName: name,
        filePath: item.filePath,
        objectUrl,
      });
      setPendingFile(file);
      setPendingCropSrc(objectUrl);
      setCropModalOpen(true);
    } catch {
      showErrorToast("Could not load image for cropping");
    } finally {
      setUploading(false);
    }
  };

  /** Click image → crop modal → Select. Copy is uploaded only when the crop was changed. */
  const handleLibraryItemActivate = (item) => {
    if (!item?.filePath) return;
    if (enableCrop) {
      beginCropForLibraryItem(item);
      return;
    }
    finishSelectAndClose(item.filePath, item.altText || "");
  };

  const handleCropConfirm = async (_dataUrl, croppedFile, meta = {}) => {
    const altFromSelect = pendingSelectMeta?.altText || "";
    const originalPath = pendingSelectMeta?.filePath || "";
    closeCropModal();

    // User left the default selection / full image — attach original, no new library file
    if (!meta.wasCropped) {
      if (!originalPath) {
        showErrorToast("Could not select image");
        return;
      }
      showSuccessToast("Image selected");
      finishSelectAndClose(originalPath, altFromSelect);
      return;
    }

    if (!croppedFile) {
      showErrorToast("Could not save selected image");
      return;
    }

    setUploading(true);
    try {
      const path = await postFileToLibrary(croppedFile, altFromSelect);
      if (path) {
        showSuccessToast("Cropped image selected");
        finishSelectAndClose(path, altFromSelect);
      }
    } catch {
      showErrorToast("Could not save selected image");
    } finally {
      setUploading(false);
    }
  };

  const saveMediaAltText = async (mediaId, newValue, previous) => {
    const next = (newValue || "").trim().slice(0, 500);
    const prev = (previous || "").trim();
    if (next === prev) return;
    try {
      const res = await axiosInstance.patch(APIs.PATCH_COMPANY_MEDIA_ALT, {
        mediaId,
        altText: next,
      });
      if (!res.data?.status) {
        showErrorToast(res.data?.message || "Could not update alt text");
        setRefreshNonce((n) => n + 1);
        return;
      }
      setItems((list) =>
        list.map((row) =>
          row.mediaId === mediaId ? { ...row, altText: res.data?.data?.altText ?? next } : row
        )
      );
    } catch {
      showErrorToast("Could not update alt text");
      setRefreshNonce((n) => n + 1);
    }
  };

  const handleDelete = async (e, mediaId) => {
    e.stopPropagation();
    if (
      !window.confirm(
        "Remove this image from the library? It will no longer be available for new selections."
      )
    )
      return;
    try {
      const res = await axiosInstance.delete(APIs.DELETE_COMPANY_MEDIA(mediaId));
      if (res.status !== 200 || !res.data?.status) {
        showErrorToast(res.data?.message || "Could not delete");
        return;
      }
      showSuccessToast("Removed from library");
      setRefreshNonce((n) => n + 1);
    } catch {
      showErrorToast("Could not delete");
    }
  };

  return (
    <>
      <ImageCropModal
        isOpen={cropModalOpen}
        imageSrc={pendingCropSrc}
        originalFile={pendingFile}
        aspect={cropAspect}
        onClose={closeCropModal}
        onConfirm={handleCropConfirm}
        title={cropModalTitle}
        subtitle="Click the image to crop, or select it as-is. A new library copy is saved only when you crop."
        confirmButtonText="Select image"
        zIndexBase={CROP_Z_INDEX_BASE}
      />
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        showButton={false}
        modalSize="modal-xl"
        minHeight="420px"
        maxHeight="92vh"
        isCentered
        fontSize="1rem"
        fontWeight="600"
        subtitleFontSize="0.875rem"
        subtitleFontWeight="400"
        bodyOverflow="hidden"
      >
        <div className={rootClass} style={{ "--ml-theme": themeColor }}>
          <div className="media-library-modal__toolbar">
            <div className="media-library-modal__tabs" role="tablist" aria-label="Media library views">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "library"}
                className={`media-library-modal__tab ${activeTab === "library" ? "media-library-modal__tab--active" : ""}`}
                onClick={() => setActiveTab("library")}
              >
                Library
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "upload"}
                className={`media-library-modal__tab ${activeTab === "upload" ? "media-library-modal__tab--active" : ""}`}
                onClick={() => setActiveTab("upload")}
              >
                Upload
              </button>
            </div>

            {activeTab === "library" && (
              <div className="media-library-modal__search-wrap">
                <i className="fa fa-search media-library-modal__search-icon" aria-hidden />
                <input
                  type="search"
                  className="media-library-modal__search"
                  placeholder="Search by file name or description…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPageNumber(1);
                  }}
                  aria-label="Search media"
                />
              </div>
            )}

            {activeTab === "library" && (
              <span className="media-library-modal__badge" aria-live="polite">
                {loading ? "Loading…" : `${totalRecords} ${totalRecords === 1 ? "image" : "images"}`}
              </span>
            )}
          </div>

          {activeTab === "library" && (
            <>
              {loading ? (
                <div className="media-library-modal__loading">
                  <div className="media-library-modal__spinner" aria-hidden />
                  <p className="mb-0" style={{ color: "var(--ml-text-muted)", fontSize: "0.9rem" }}>
                    Loading your library…
                  </p>
                </div>
              ) : items.length === 0 ? (
                <div className="media-library-modal__empty">
                  <div className="media-library-modal__empty-icon" aria-hidden>
                    <i className="fa fa-images" />
                  </div>
                  <div className="media-library-modal__empty-title">No images yet</div>
                  <p className="media-library-modal__empty-text mb-0">
                    Upload photos here once and reuse them across menu items and other content.
                    Switch to the <strong>Upload</strong> tab to add your first image
                    {enableCrop
                      ? ", then click it here to crop and select for the product."
                      : "."}
                  </p>
                </div>
              ) : (
                <>
                  <div className="media-library-modal__grid-scroll">
                    <div className="media-library-modal__grid">
                      {items.map((item) => (
                        <div key={item.mediaId} className="media-library-modal__card-wrap">
                          <div
                            className="media-library-modal__card"
                            onClick={() => handleLibraryItemActivate(item)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleLibraryItemActivate(item);
                              }
                            }}
                          >
                            <img
                              src={item.fileUrl}
                              alt={item.altText || ""}
                              className="media-library-modal__card-img"
                            />
                            <div className="media-library-modal__card-shade" aria-hidden />
                            <div className="media-library-modal__card-footer">
                              <div className="media-library-modal__card-name" title={item.originalFileName}>
                                {item.originalFileName}
                              </div>
                              <input
                                type="text"
                                className="media-library-modal__alt-input"
                                defaultValue={item.altText || ""}
                                key={`alt-${item.mediaId}-${item.altText ?? ""}-${refreshNonce}`}
                                placeholder="Alt text (ordering / accessibility)"
                                aria-label={`Alt text for ${item.originalFileName}`}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                onBlur={(e) =>
                                  saveMediaAltText(item.mediaId, e.target.value, item.altText)
                                }
                              />
                            </div>
                            <button
                              type="button"
                              className="media-library-modal__delete"
                              title="Delete from library"
                              onClick={(e) => handleDelete(e, item.mediaId)}
                            >
                              <i className="fa fa-trash" style={{ fontSize: "13px" }} aria-hidden />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {totalRecords > PAGE_SIZE && (
                    <div className="media-library-modal__pager">
                      <span className="media-library-modal__pager-info">
                        Page {pageNumber} of {totalPages}
                      </span>
                      <div className="media-library-modal__pager-btns">
                        <button
                          type="button"
                          className="media-library-modal__page-btn"
                          disabled={pageNumber <= 1 || loading}
                          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="media-library-modal__page-btn"
                          disabled={pageNumber >= totalPages || loading}
                          onClick={() => setPageNumber((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === "upload" && (
            <div className="media-library-modal__upload">
              <div className="media-library-modal__upload-icon" aria-hidden>
                <i className="fa fa-image" />
              </div>
              <div className="media-library-modal__upload-title">Add to your library</div>
              <p className="media-library-modal__upload-desc mb-0">
                {enableCrop
                  ? "Select a file from your device. It is saved as-is to your library. On the Library tab, click an image to crop it and select the result for the product."
                  : "Select an image file. It is saved to your company library only — go to the Library tab to choose which image to use."}
              </p>
              <label className="media-library-modal__alt-label" htmlFor="media-lib-upload-alt">
                Image description (alt text)
              </label>
              <input
                id="media-lib-upload-alt"
                type="text"
                className="media-library-modal__alt-input media-library-modal__alt-input--upload"
                value={uploadAltText}
                onChange={(e) => setUploadAltText(e.target.value.slice(0, 500))}
                placeholder="Shown on online ordering for accessibility (optional)"
                maxLength={500}
                disabled={uploading}
              />
              <button
                type="button"
                className="media-library-modal__upload-btn"
                disabled={uploading}
                onClick={() => uploadInputRef.current?.click()}
              >
                {uploading ? "Please wait…" : "Choose image"}
              </button>
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleUploadFileChosen}
              />
            </div>
          )}
        </div>
      </CustomModal>
    </>
  );
};

export default MediaLibraryModal;
